import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Image from '../../../components/AppImage';

const CategoryProducts = ({ category, products }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  const filteredProducts = products
    .filter(product => 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.sku.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const aValue = a[sortBy];
      const bValue = b[sortBy];
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleProductClick = (productId) => {
    navigate(`/product-catalog-management?product=${productId}`);
  };

  if (!category) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Icon name="Package" size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
          <p className="text-muted-foreground">Select a category to view its products</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white rounded-lg border border-border">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-md bg-surface flex items-center justify-center">
              <Icon name={category.icon || 'Package'} size={16} className="text-primary" />
            </div>
            <div>
              <h3 className="font-medium text-foreground">{category.name} Products</h3>
              <p className="text-sm text-muted-foreground">{products.length} products found</p>
            </div>
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/add-product')}
            iconName="Plus"
            iconPosition="left"
          >
            Add Product
          </Button>
        </div>

        {/* Search and Sort */}
        <div className="flex items-center space-x-3">
          <div className="flex-1">
            <Input
              type="search"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleSort('name')}
              className="flex items-center space-x-1"
            >
              <span>Name</span>
              {sortBy === 'name' && (
                <Icon 
                  name={sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                  size={14} 
                />
              )}
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleSort('price')}
              className="flex items-center space-x-1"
            >
              <span>Price</span>
              {sortBy === 'price' && (
                <Icon 
                  name={sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} 
                  size={14} 
                />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Products List */}
      <div className="flex-1 overflow-y-auto">
        {filteredProducts.length > 0 ? (
          <div className="divide-y divide-border">
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                className="p-4 hover:bg-muted/50 cursor-pointer transition-micro"
                onClick={() => handleProductClick(product.id)}
              >
                <div className="flex items-center space-x-4">
                  {/* Product Image */}
                  <div className="w-12 h-12 rounded-md overflow-hidden bg-surface flex-shrink-0">
                    <Image
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Product Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <h4 className="font-medium text-foreground truncate">{product.name}</h4>
                        <p className="text-sm text-muted-foreground">SKU: {product.sku}</p>
                      </div>
                      
                      <div className="text-right ml-4">
                        <p className="font-semibold text-foreground">${product.price.toFixed(2)}</p>
                        <p className="text-sm text-muted-foreground">Stock: {product.stock}</p>
                      </div>
                    </div>

                    {/* Status and Actions */}
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          product.status === 'active' ?'bg-success/10 text-success' 
                            : product.status === 'draft' ?'bg-warning/10 text-warning' :'bg-error/10 text-error'
                        }`}>
                          <div className={`w-1.5 h-1.5 rounded-full mr-1 ${
                            product.status === 'active' ?'bg-success' 
                              : product.status === 'draft' ?'bg-warning' :'bg-error'
                          }`}></div>
                          {product.status.charAt(0).toUpperCase() + product.status.slice(1)}
                        </span>
                        
                        {product.stock <= 10 && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-warning/10 text-warning">
                            <Icon name="AlertTriangle" size={12} className="mr-1" />
                            Low Stock
                          </span>
                        )}
                      </div>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleProductClick(product.id);
                        }}
                        iconName="ExternalLink"
                        iconPosition="right"
                        className="opacity-0 group-hover:opacity-100 transition-micro"
                      >
                        View
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <Icon name="Package" size={48} className="mx-auto mb-4 text-muted-foreground opacity-50" />
              <h4 className="font-medium text-foreground mb-2">No Products Found</h4>
              <p className="text-muted-foreground mb-4">
                {searchTerm 
                  ? 'No products match your search criteria' :'This category doesn\'t have any products yet'
                }
              </p>
              <Button
                variant="outline"
                onClick={() => navigate('/add-product')}
                iconName="Plus"
                iconPosition="left"
              >
                Add First Product
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryProducts;